import React, { useEffect, useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { InputWrap, LabelWrapper, Error } from "../TextInput/styled";
import { Input, Label } from "theme-ui";
import { useGoogleMapsLoader } from "hooks/googleMapsLoader";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY ?? "";

const GooglePlaces = ({ 
  name, 
  label, 
  value, 
  description, 
  required,
  disabled, 
  readonly, 
  placeholder = "Search...",     
  $customStyles, 
  $errors, 
  $responseErrors, 
  onChange 
}:{
  name?: string;
  label?: string | React.ReactNode;  
  value?: string | number;
  description?: string; 
  required?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  placeholder?: string; 
  $customStyles?: any;
  $responseErrors?: any;
  $errors?: any;
  onChange?: (e?: any) => void;
}) => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null); // Use a ref for input field

  const onLoad = (autoC: google.maps.places.Autocomplete) => {
    setAutocomplete(autoC);
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;

    const place: any = autocomplete.getPlace();
    if (place.formatted_address) { 
      const addr = place?.address_components;
      const geo = place?.geometry?.location;
      
      const formatted = place?.formatted_address || "";
      const name =
        formatted.startsWith(place.name)
          ? formatted
          : `${place.name}, ${formatted}`;
      setInputValue(name);

      if (typeof onChange === "function") {
        const retVal = {
          address: {
            line1: [
              addr?.find((c: any) => c.types.includes("street_number"))?.long_name,
              addr?.find((c: any) => c.types.includes("route"))?.long_name,
            ].filter(Boolean).join(" ").trim() || place.name,          
            city: addr?.find((c: any) => c.types.includes("locality") || c.types.includes("sublocality"))?.long_name,
            state: addr?.find((c: any) => c.types.includes("administrative_area_level_1"))?.short_name,
            postalCode: addr?.find((c: any) => c.types.includes("postal_code"))?.long_name,
            country: addr?.find((c: any) => c.types.includes("country"))?.short_name,
          },
          latitude: geo?.lat(),
          longitude: geo?.lng(),
        };
        onChange(retVal);
      }
    }
  };

  useEffect(() => {
    if (!value) {  // Ensure `undefined` or `null` are treated as an empty string
      setInputValue("");
      if (inputRef.current) {
        inputRef.current.value = ""; // Clears Autocomplete input field
      }
      if (typeof onChange === "function") {
        onChange(null);
      }
    } else {
      setInputValue(String(value || "")); // Avoid "undefined" in input field
    }
  }, [value]);

  if (loadError || !isLoaded) return null;

  return (
    <InputWrap $customStyles={$customStyles}>
      {label && 
        <LabelWrapper>
          <Label>{label}</Label>
          {required ? <span>*</span>  : ''}         
        </LabelWrapper>
      }
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <Input 
          ref={inputRef} // Attach the ref to the input
          type="text" 
          name={name} 
          disabled={disabled} 
          readOnly={readonly} 
          placeholder={placeholder} 
          value={inputValue} 
          onChange={(e?: any) => setInputValue(e.target.value)} 
          style={{paddingTop:"8px"}}                     
        />        
      </Autocomplete>
      {description && <p><small>{description}</small></p>}       
      {$errors && <Error>{$errors}</Error>}
    </InputWrap>
  );
};

export default GooglePlaces;